import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import * as uuid from 'uuid';

import {
  createNewRecipientExpr,
  currentEvaluatorVersion,
  NotificationSchedule,
  RecipientType,
} from 'notifications/notification-schedule/NotificationScheduleExpressionUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER, GRAY, GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import NotificationScheduleEditorRow, {
  createNewDeliveryDateExprFromUI,
  DeliveryDirection,
  DeliveryTimeParams,
} from './NotificationScheduleEditorRow';

export interface DeliveryDateParams {
  days: number;
  direction: DeliveryDirection;
}

// TODO(Nao): Make default params optional
export interface NotificationScheduleEditorProps {
  notificationSchedules: Array<NotificationSchedule>;
  defaultDeliveryTime: DeliveryTimeParams;
  defaultDeliveryDate: DeliveryDateParams;
  defaultRecipient: RecipientType;
  onUpdate: (notificationSchedules: Array<NotificationSchedule>) => void;
  readonly?: boolean;
}

export default function NotificationScheduleEditor(props: NotificationScheduleEditorProps): JSX.Element {
  const isReadOnly = props.readonly ?? false;
  const onUpdateNotificationSchedule = (
    newNotificationSchedule: NotificationSchedule,
    rowIndex: number
  ): void => {
    const newNotificationSchedules = props.notificationSchedules.map((notificationSchedule, index) => {
      if (index === rowIndex) {
        return newNotificationSchedule;
      }
      return notificationSchedule;
    });
    props.onUpdate(newNotificationSchedules);
  };

  const deleteScheduleEditorRow = (rowIndex: number): void => {
    const newSchedules = props.notificationSchedules.map((schedule) => schedule);
    newSchedules.splice(rowIndex, 1);
    props.onUpdate(newSchedules);
  };

  const addScheduleEditorRow = (): void => {
    const newSchedules = props.notificationSchedules.map((schedule) => schedule);
    const recipientExpr = createNewRecipientExpr(props.defaultRecipient);
    const {days, direction} = props.defaultDeliveryDate;
    const deliveryDateExpr = createNewDeliveryDateExprFromUI(days, direction, props.defaultDeliveryTime);
    const evaluatorVersion = currentEvaluatorVersion();
    const newSchedule = {
      recipientExpr,
      deliveryDateExpr,
      evaluatorVersion,
    };
    newSchedules.push(newSchedule);
    props.onUpdate(newSchedules);
  };

  return (
    <div {...getTestProps({component: 'NotificationScheduleEditor', element: 'root'})}>
      {props.notificationSchedules.map((notificationSchedule, index) => (
        <div
          className={css(styles.background)}
          key={uuid.v4()}
          {...getTestProps({
            component: 'NotificationScheduleEditor',
            element: 'row',
            index: index.toString(),
          })}
        >
          <NotificationScheduleEditorRow
            notificationSchedule={notificationSchedule}
            defaultDeliveryTime={props.defaultDeliveryTime}
            onUpdate={(notificationSchedule: NotificationSchedule) => {
              onUpdateNotificationSchedule(notificationSchedule, index);
            }}
            readOnly={isReadOnly}
          />
          {!isReadOnly && (
            <div
              className={css(styles.removeButton)}
              data-test-key="removeButton"
              {...getTestProps({
                component: 'NotificationScheduleEditor',
                element: 'removeButton',
                index: index.toString(),
              })}
              onClick={() => {
                deleteScheduleEditorRow(index);
              }}
            >
              <BenchlingIcon icon="delete" altText="Delete" />
            </div>
          )}
        </div>
      ))}
      {!isReadOnly && (
        <Button
          className={css(styles.addButton)}
          buttonStyle={ButtonStyle.TEXT}
          size={ButtonSize.MEDIUM}
          icon="compose"
          text="Add Schedule"
          onClick={addScheduleEditorRow}
          data-test-key="addScheduleButton"
        />
      )}
    </div>
  );
}
const styles = StyleSheet.create({
  background: {
    backgroundColor: GRAY_LIGHTER,
    borderRadius: '4px',
    marginBottom: '12px',
    padding: '6px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  addButton: {
    marginBottom: '10px',
  },
  removeButton: {
    cursor: 'pointer',
    color: GRAY,
    ':hover': {
      color: GRAY_DARKER,
    },
    padding: '16px',
  },
});
